import { get, merge } from 'lodash';
import { deepClone } from '../util/deepClone'

// Object containing common style properties that are used in multiple style objects
const commonStyleProperties = {
	color: {
		accent: {
			primary: '#777',
			secondary: '#CCC'
		},
		band: {
			background: 'rgba(0, 0, 0, 0.05)',
			line: 'rgba(200, 200, 200, 0.5)'
		},
		primary: 'steelblue',
		negative: 'firebrick',
		neutral: '#999999',
		positive: 'forestgreen'
	},
	line: {
		width: 1.5
	},
	panel: {
		heightRatio: 0.25,
		yAxisPadding: {
			bottom: 0,
			top: 0
		}
	},
	font: {
		family: 'Arial, Helvetica, sans-serif'
	},
	sessionBreak: {
		color: null,
		lineDash: '2',
		width: null,
		border: {
			lineDash: null,
			color: null,
			width: null
		}
	}
};

// Holds user-defined overrides for common style properties located in `commonStyleProperties`
let commonStyleOverrides = {};

/**
* Updates the common style overrides.
* @param {Object} overrides - Object containing style property overrides.
*/
export const setCommonStyles = overrides => {
	commonStyleOverrides = {}; // reset for jest testing
	merge(commonStyleOverrides, overrides);
};

/**
 * Retrieves the current value of a style property, considering any overrides.
 * This function looks up a property in the `commonStyleOverrides` object first.
 * If the property is not found in the overrides, it falls back to the default common styles.
 *
 * @param {string} path - The dot-separated path to the property in the commonStyleProperties style object.
 * @returns The value of the style property, either from the overrides or default.
 *
 * Example Usage:
 *   getCommonStyle('line.width') - Retrieves the line width.
 */
export const getCommonStyle = path => {
	// First, check for an override
	const override = get(commonStyleOverrides, path);
	if (override !== undefined) {
		// Return a deep copy for objects to avoid mutations
		return typeof override === 'object' ? deepClone(override) : override;
	}

	// If no override, fall back to the default property
	const property = get(commonStyleProperties, path);
	// Return a deep copy for objects
	return typeof property === 'object' ? deepClone(property) : property;
};
